/**
 * SupplementSections — generic renderer for merged supplement data.
 *
 * When the detail panel merges supplementary tool results (comments,
 * attachments, reviews, results, etc.) into a primary resource, those
 * arrive as underscore-prefixed keys (_comments, _attachments, etc.).
 *
 * This component detects and renders them with appropriate formatting.
 * Used by all provider renderers for consistent supplement display.
 */

import { MessageSquare, Paperclip, GitCommit, FileText, ListChecks, Eye, Zap } from 'lucide-react'
import { MessageContent } from '../MessageContent'

interface SupplementSectionsProps {
  data: Record<string, unknown>
}

// Map of supplement key → display config
const SECTION_CONFIG: Record<string, { icon: typeof MessageSquare; label: string }> = {
  _fileContent: { icon: FileText, label: 'File Content' },
  _comments: { icon: MessageSquare, label: 'Comments' },
  _reviews: { icon: Eye, label: 'Reviews' },
  _attachments: { icon: Paperclip, label: 'Attachments' },
  _commits: { icon: GitCommit, label: 'Commits' },
  _files: { icon: FileText, label: 'Changed Files' },
  _results: { icon: ListChecks, label: 'Test Results' },
  _transitions: { icon: Zap, label: 'Available Transitions' },
  _versions: { icon: FileText, label: 'Version History' },
  _changelog: { icon: FileText, label: 'Changelog' },
  _console: { icon: FileText, label: 'Console Output' },
  _supplement: { icon: FileText, label: 'Additional Details' },
}

function str(val: unknown): string {
  if (!val) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number') return String(val)
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>
    if (typeof obj.displayName === 'string') return obj.displayName
    if (typeof obj.display_name === 'string') return obj.display_name
    if (typeof obj.name === 'string') return obj.name
    if (typeof obj.login === 'string') return obj.login
    if (typeof obj.author === 'string') return obj.author
    if (typeof obj.emailAddress === 'string') return obj.emailAddress
  }
  return ''
}

export function SupplementSections({ data }: SupplementSectionsProps) {
  const sections = Object.entries(data)
    .filter(([key]) => key.startsWith('_'))
    // Filter out state-update patches and internal metadata
    .filter(([key]) => !key.startsWith('_update_') && key !== '_statusUpdate' && key !== '_assigneeUpdate' && key !== '_fileName' && key !== '_steps' && key !== '_testCases' && key !== '_new_comment')
    .map(([key, value]) => ({ key, value }))

  if (sections.length === 0) return null

  return (
    <>
      {sections.map(({ key, value }) => {
        const config = SECTION_CONFIG[key] || { icon: FileText, label: key.slice(1).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }
        const Icon = config.icon

        return (
          <div key={key}>
            <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {config.label}
              {Array.isArray(value) && ` (${value.length})`}
            </div>
            <SupplementContent sectionKey={key} value={value} />
          </div>
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------------
// Section-specific renderers
// ---------------------------------------------------------------------------

function SupplementContent({ sectionKey, value }: { sectionKey: string; value: unknown }) {
  // File content (GitHub README, source files, docs)
  if (sectionKey === '_fileContent' && typeof value === 'string') {
    return (
      <div className="rounded-lg bg-foreground/[0.03] border border-subtle p-4 text-sm text-foreground leading-relaxed max-h-[500px] overflow-y-auto">
        <MessageContent content={value} />
      </div>
    )
  }

  // Comments (Jira, Confluence, GitHub, Bitbucket)
  if (sectionKey === '_comments' && Array.isArray(value)) {
    return <CommentsList comments={value} />
  }

  // Reviews (GitHub PRs)
  if (sectionKey === '_reviews' && Array.isArray(value)) {
    return <ReviewsList reviews={value} />
  }

  // Transitions (Jira workflow)
  if (sectionKey === '_transitions' && Array.isArray(value)) {
    return <TransitionsList transitions={value} />
  }

  // Test results (TestRail)
  if (sectionKey === '_results' && Array.isArray(value)) {
    return <TestResultsList results={value} />
  }

  // Console output (Jenkins)
  if (sectionKey === '_console') {
    const text = typeof value === 'string' ? value
      : typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2)
      : String(value)
    return (
      <pre className="rounded-lg bg-code-soft text-muted-foreground p-3 text-xs font-mono overflow-auto max-h-64 leading-relaxed">
        {text}
      </pre>
    )
  }

  // Commits (GitHub)
  if (sectionKey === '_commits' && Array.isArray(value)) {
    return <CommitsList commits={value} />
  }

  // Attachments (Confluence)
  if (sectionKey === '_attachments' && Array.isArray(value)) {
    return <AttachmentsList attachments={value} />
  }

  // Fallback: formatted JSON
  if (Array.isArray(value)) {
    return (
      <div className="space-y-1">
        {value.map((item, i) => (
          <div key={i} className="bg-foreground/[0.03] border border-subtle rounded-lg p-2.5 text-xs">
            <pre className="font-mono text-foreground whitespace-pre-wrap overflow-auto max-h-32">
              {JSON.stringify(item, null, 2)}
            </pre>
          </div>
        ))}
      </div>
    )
  }

  return (
    <pre className="rounded-lg bg-foreground/[0.04] border border-subtle p-3 text-xs font-mono text-foreground overflow-auto max-h-48">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function CommentsList({ comments }: { comments: unknown[] }) {
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {comments.map((comment, i) => {
        const c = comment as Record<string, unknown>
        const author = str(c.author) || str(c.created_by) || str(c.user) || 'Unknown'
        const body = (c.body as string) || (c.content as string) || (c.text as string) || ''
        const created = (c.created as string) || (c.created_at as string) || (c.created_on as string) || ''

        return (
          <div key={(c.id as string) || i} className="bg-foreground/[0.03] border border-subtle rounded-lg p-2.5 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-foreground">{author}</span>
              {created && (
                <span className="text-subtle-foreground">{new Date(created).toLocaleDateString()}</span>
              )}
            </div>
            <div className="text-muted-foreground">
              {body ? <MessageContent content={body} /> : <span className="text-subtle-foreground italic">No content</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ReviewsList({ reviews }: { reviews: unknown[] }) {
  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {reviews.map((review, i) => {
        const r = review as Record<string, unknown>
        const author = str(r.user) || str(r.author) || 'Unknown'
        const state = (r.state as string) || ''
        const body = (r.body as string) || ''
        const stateColor = state === 'APPROVED' ? 'text-success-foreground bg-success-soft'
          : state === 'CHANGES_REQUESTED' ? 'text-destructive-foreground bg-destructive-soft'
          : 'text-muted-foreground bg-foreground/[0.04]'

        return (
          <div key={(r.id as string) || i} className="bg-foreground/[0.03] border border-subtle rounded-lg p-2.5 text-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-foreground">{author}</span>
              {state && <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${stateColor}`}>{state}</span>}
            </div>
            {body && <div className="text-muted-foreground"><MessageContent content={body} /></div>}
          </div>
        )
      })}
    </div>
  )
}

function TransitionsList({ transitions }: { transitions: unknown[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {transitions.map((t, i) => {
        const tr = t as Record<string, unknown>
        const name = (tr.name as string) || str(tr)
        return (
          <span key={(tr.id as string) || i} className="rounded-full bg-accent-soft border border-accent/30 px-2.5 py-0.5 text-xs text-accent-soft-foreground font-medium">
            {name}
          </span>
        )
      })}
    </div>
  )
}

function TestResultsList({ results }: { results: unknown[] }) {
  const STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: 'Passed', color: 'text-success-foreground bg-success-soft' },
    2: { label: 'Blocked', color: 'text-warning-foreground bg-warning-soft' },
    3: { label: 'Untested', color: 'text-muted-foreground bg-foreground/[0.04]' },
    4: { label: 'Retest', color: 'text-info-foreground bg-info-soft' },
    5: { label: 'Failed', color: 'text-destructive-foreground bg-destructive-soft' },
  }

  return (
    <div className="space-y-1 max-h-64 overflow-y-auto">
      {results.map((result, i) => {
        const r = result as Record<string, unknown>
        const statusId = r.status_id as number
        const status = STATUS_MAP[statusId] || { label: `Status ${statusId}`, color: 'text-muted-foreground bg-foreground/[0.04]' }
        const comment = (r.comment as string) || ''
        const elapsed = (r.elapsed as string) || ''

        return (
          <div key={(r.id as string) || i} className="flex items-center gap-2 bg-foreground/[0.03] border border-subtle rounded px-2.5 py-1.5 text-xs">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${status.color}`}>{status.label}</span>
            {r.test_id != null && <span className="text-subtle-foreground font-mono">T{String(r.test_id)}</span>}
            {elapsed && <span className="text-subtle-foreground">{elapsed}</span>}
            {comment && <span className="text-muted-foreground truncate flex-1">{comment}</span>}
          </div>
        )
      })}
    </div>
  )
}

function CommitsList({ commits }: { commits: unknown[] }) {
  return (
    <div className="space-y-1 max-h-48 overflow-y-auto">
      {commits.map((commit, i) => {
        const c = commit as Record<string, unknown>
        const sha = ((c.sha as string) || (c.id as string) || '').slice(0, 7)
        const message = (c.message as string) || ((c.commit as Record<string, unknown>)?.message as string) || ''
        const author = str(c.author) || str((c.commit as Record<string, unknown>)?.author) || ''

        return (
          <div key={sha || i} className="flex items-center gap-2 bg-foreground/[0.03] border border-subtle rounded px-2.5 py-1.5 text-xs">
            {sha && <span className="font-mono text-accent shrink-0">{sha}</span>}
            <span className="text-foreground truncate flex-1">{message.split('\n')[0]}</span>
            {author && <span className="text-subtle-foreground shrink-0">{author}</span>}
          </div>
        )
      })}
    </div>
  )
}

function AttachmentsList({ attachments }: { attachments: unknown[] }) {
  return (
    <div className="space-y-1">
      {attachments.map((att, i) => {
        const a = att as Record<string, unknown>
        const title = (a.title as string) || (a.filename as string) || (a.name as string) || 'Attachment'
        const size = a.fileSize || a.size
        const mediaType = (a.mediaType as string) || (a.content_type as string) || ''

        return (
          <div key={(a.id as string) || i} className="flex items-center gap-2 bg-foreground/[0.03] border border-subtle rounded px-2.5 py-1.5 text-xs">
            <Paperclip className="w-3 h-3 text-subtle-foreground shrink-0" />
            <span className="text-foreground font-medium truncate flex-1">{title}</span>
            {mediaType && <span className="text-subtle-foreground">{mediaType.split('/').pop()}</span>}
            {size != null && <span className="text-subtle-foreground">{formatBytes(Number(size))}</span>}
          </div>
        )
      })}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}
